import * as React from 'react'
import { Form, Input, Tabs, Spin } from 'antd'
import { get, set, cloneDeep, take } from 'lodash'
import produce from 'immer'
import { showError, resourceNotFound, ValidationError } from 'helpers/errors'
import { createLabelFactory, createFieldFactory, createHandleSubmit, isReadOnly } from 'helpers/formViews'
import { HARD_SPACE } from 'helpers/utils'
import xmlMapItemFromOptions from 'options/suppliers/xmlMapItemFrom'
import xmlMapItemToOptions from 'options/suppliers/xmlMapItemTo'
import { getUserPermission } from 'helpers/auth'
import { t } from 'helpers/i18n'
import { stopEvent } from 'helpers/events'
import Select, { Option } from 'elements/Select'
import Icon from 'elements/Icon'
import Checkbox from 'elements/Checkbox'
import FormButtons from 'elements/FormButtons'
import { Row, Col } from 'elements/Grid'

class FormView extends React.Component {
  state = {}

  constructor(props) {
    super(props)

    this.handleSubmit = createHandleSubmit(this)
  }

  componentDidMount() {
    this.fetchItem()
  }

  getItemId = () =>
    [
      this.state.item?.id,
      this.props.linkTargetRecord?.supplierId,
      this.props.linkTargetRecord?.itemSupplierId,
      this.props.linkTargetRecord?.productSupplierId,
      this.props.linkTargetRecord?.defaultSupplierId,
      this.props.linkTargetRecord?.preferredSupplierId,
      this.props.linkTargetRecord?.orderSupplierId,
    ].find(Boolean)

  fetchItem = async ({ itemId = this.getItemId() } = {}) => {
    try {
      const responses = await Promise.all([
        itemId
          ? this.props.getItem(itemId).catch((error) => {
              this.props.onCancel?.()
              throw error
            })
          : !this.props.linkTargetRecord
            ? this.props.newItem({})
            : resourceNotFound(this),
        this.props.getSettings({ type: this.props.settingsType }),
        this.props.getSupplierIntegrations(),
      ])

      const item = cloneDeep(responses[0].value.data)

      item.xmlMap = item.xmlMap || []
      ;[...Array(4).keys()].forEach(() => item.xmlMap.push({ from: 0, to: 0 }))
      item.xmlMap = take(item.xmlMap, 4)

      this.setState({
        item,
        fieldSettings: responses[1].value.data.fieldSettings,
        supplierIntegrations: get(responses[2], 'value.data.items', []),
      })
    } catch (error) {
      showError({ error })
    }
  }

  setItemValue = (name, value) =>
    this.setState(
      produce((draft) => {
        set(draft.item, name, value)

        this.props.form.setFieldsValue({ [name]: value })
      })
    )

  render() {
    const { item, fieldSettings } = this.state

    if (!item || !fieldSettings) {
      return <Spin />
    }

    const createLabel = createLabelFactory(fieldSettings)
    const createFieldDecorator = createFieldFactory(this.props.form, item, fieldSettings)

    const readOnly =
      isReadOnly(this) || (getUserPermission('Suppliers') !== 'Edit' && getUserPermission('Master') !== 'Yes')

    return (
      <Form layout="vertical" colon={false} onSubmit={readOnly ? stopEvent : this.handleSubmit}>
        <div className="form-items-container">
          <ValidationError errors={this.state.validationErrors} />
          <Row>
            <Col md={8} xs={24}>
              <Form.Item label={createLabel('name')}>
                {createFieldDecorator('name')(<Input readOnly={readOnly} autoFocus={!readOnly} />)}
              </Form.Item>
              <Form.Item label={createLabel('number')}>
                {createFieldDecorator('number')(<Input readOnly={readOnly} />)}
              </Form.Item>
              <Form.Item label={createLabel('accountNumber')}>
                {createFieldDecorator('accountNumber')(<Input readOnly={readOnly} />)}
              </Form.Item>
              <Form.Item label={createLabel('defaultPo')}>
                {createFieldDecorator('defaultPo')(<Input readOnly={readOnly} />)}
              </Form.Item>
            </Col>
            <Col md={8} xs={24}>
              <Form.Item label={createLabel('contactFirstName')}>
                {createFieldDecorator('contactFirstName')(<Input readOnly={readOnly} />)}
              </Form.Item>
              <Form.Item label={createLabel('contactLastName')}>
                {createFieldDecorator('contactLastName')(<Input readOnly={readOnly} />)}
              </Form.Item>
              <Form.Item label={createLabel('contactEmail')}>
                {createFieldDecorator('contactEmail')(<Input type="email" readOnly={readOnly} />)}
              </Form.Item>
              <Form.Item label={createLabel('secondaryEmail')}>
                {createFieldDecorator('secondaryEmail')(<Input type="email" readOnly={readOnly} />)}
              </Form.Item>
            </Col>
            <Col md={8} xs={24}>
              <Form.Item label={HARD_SPACE}>
                {createFieldDecorator('sendAsEmail', { valuePropName: 'checked' })(
                  <Checkbox readOnly={readOnly}>{createLabel('sendAsEmail')}</Checkbox>
                )}
              </Form.Item>
              <Form.Item label={HARD_SPACE}>
                {createFieldDecorator('allowNonStock', { valuePropName: 'checked' })(
                  <Checkbox readOnly={readOnly}>{createLabel('allowNonStock')}</Checkbox>
                )}
              </Form.Item>
              <Form.Item label={HARD_SPACE}>
                {createFieldDecorator('active', { valuePropName: 'checked' })(
                  <Checkbox readOnly={readOnly}>{createLabel('active')}</Checkbox>
                )}
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label={createLabel('shippingInstructions')}>
            {createFieldDecorator('shippingInstructions')(<Input.TextArea rows={5} readOnly={readOnly} />)}
          </Form.Item>
          <Tabs
            activeKey={this.state.tabsActiveKey || 'address'}
            onChange={(tabsActiveKey) => this.setState({ tabsActiveKey })}
          >
            <Tabs.TabPane key="address" tab={t('address')} forceRender>
              <Row>
                <Col md={8} xs={24}>
                  <Form.Item label={createLabel('address.address1')}>
                    {createFieldDecorator('address.address1')(<Input readOnly={readOnly} />)}
                  </Form.Item>
                  <Form.Item label={createLabel('address.city')}>
                    {createFieldDecorator('address.city')(<Input readOnly={readOnly} />)}
                  </Form.Item>
                  <Form.Item label={createLabel('address.state')}>
                    {createFieldDecorator('address.state')(<Input readOnly={readOnly} />)}
                  </Form.Item>
                  <Form.Item label={createLabel('address.country')}>
                    {createFieldDecorator('address.country')(<Input readOnly={readOnly} />)}
                  </Form.Item>
                  <Form.Item label={createLabel('address.zip')}>
                    {createFieldDecorator('address.zip')(<Input readOnly={readOnly} />)}
                  </Form.Item>
                </Col>
                <Col md={8} xs={24}>
                  <Form.Item label={createLabel('address.phone')}>
                    {createFieldDecorator('address.phone')(<Input type="tel" readOnly={readOnly} />)}
                  </Form.Item>
                  <Form.Item label={createLabel('address.phone2')}>
                    {createFieldDecorator('address.phone2')(<Input type="tel" readOnly={readOnly} />)}
                  </Form.Item>
                  <Form.Item label={createLabel('address.fax')}>
                    {createFieldDecorator('address.fax')(<Input type="tel" readOnly={readOnly} />)}
                  </Form.Item>
                </Col>
              </Row>
            </Tabs.TabPane>
            <Tabs.TabPane key="integration" tab={t('integration')} forceRender>
              <Row>
                <Col md={8} xs={24}>
                  <Form.Item label={createLabel('linkedSupplierTenantId')}>
                    {createFieldDecorator('linkedSupplierTenantId')(
                      <Select readOnly={readOnly} allowClear={false}>
                        <Option value={0}>{t('none')}</Option>
                        {Object.entries(item.validSupplierPortals).map(([name, id]) => (
                          <Option key={id} value={id}>
                            {name}
                          </Option>
                        ))}
                      </Select>
                    )}
                  </Form.Item>
                </Col>
                {this.props.customer.generalSettings.enableExternalItemStatusRequest && (
                  <Col md={8} xs={24}>
                    <Form.Item label={createLabel('cxmlItemStatusRequestRecipient')}>
                      {createFieldDecorator('cxmlItemStatusRequestRecipient')(
                        <Select placeholder={t('none')} readOnly={readOnly}>
                          <Option value="">{t('none')}</Option>
                          {(this.state.supplierIntegrations ?? [])
                            .filter((each) => each.supplierIntegrationType === 'ItemStatusRequestRecipient')
                            .map((each) => (
                              <Option key={each.name} value={each.name}>
                                {each.name}
                              </Option>
                            ))}
                        </Select>
                      )}
                    </Form.Item>
                  </Col>
                )}
              </Row>
              <Row>
                <Col md={8} xs={24}>
                  <Form.Item label={createLabel('cxmlPunchoutRecipient')}>
                    {createFieldDecorator('cxmlPunchoutRecipient')(
                      <Select placeholder={t('none')} readOnly={readOnly}>
                        <Option value="">{t('none')}</Option>
                        {(this.state.supplierIntegrations ?? [])
                          .filter((each) => each.supplierIntegrationType === 'PunchoutRecipient')
                          .map((each) => (
                            <Option key={each.name} value={each.name}>
                              {each.name}
                            </Option>
                          ))}
                      </Select>
                    )}
                  </Form.Item>
                  <Form.Item className="mb-12">
                    {createFieldDecorator('sendAsCxml', { valuePropName: 'checked' })(
                      <Checkbox readOnly={readOnly}>{createLabel('sendAsCxml')}</Checkbox>
                    )}
                  </Form.Item>
                  <Form.Item label={createLabel('cxmlRecipient')} className="whitespace-nowrap">
                    {createFieldDecorator('cxmlRecipient')(
                      <Select
                        placeholder={t('none')}
                        disabled={!this.props.form.getFieldValue('sendAsCxml')}
                        readOnly={readOnly}
                        allowClear={false}
                      >
                        <Option value="none">{t('none')}</Option>
                        {(this.state.supplierIntegrations ?? [])
                          .filter((each) => each.supplierIntegrationType === 'XmlOrderRecipient')
                          .map((each) => (
                            <Option key={each.name} value={each.name}>
                              {each.name}
                            </Option>
                          ))}
                      </Select>
                    )}
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item label={t('mappedFields')}>
                {item.xmlMap.map((each, i) => (
                  <Row key={`xmlMap${i}`} gutter={6}>
                    <Col xs={12} md={8}>
                      <div className="mb-6 whitespace-nowrap">
                        <Select
                          onChange={(value) => this.setItemValue(`xmlMap[${i}].from`, value)}
                          placeholder={t('none')}
                          disabled={!this.props.form.getFieldValue('sendAsCxml')}
                          value={get(item, `xmlMap[${i}].from`)}
                          readOnly={readOnly}
                        >
                          {Object.entries(xmlMapItemFromOptions).map((entry, j) => (
                            <Option key={entry[0]} value={j}>
                              {t(entry[1])}
                            </Option>
                          ))}
                        </Select>
                      </div>
                    </Col>
                    <Col xs={12} md={8}>
                      <div className="whitespace-nowrap mb-6">
                        <span className="mr-6">
                          <Icon type="ArrowForward" />
                        </span>
                        <Select
                          key={`xmlMap${i}`}
                          onChange={(value) => this.setItemValue(`xmlMap[${i}].to`, value)}
                          placeholder={t('none')}
                          disabled={!this.props.form.getFieldValue('sendAsCxml')}
                          value={get(item, `xmlMap[${i}].to`)}
                          style={{ width: 'calc(100% - 20px)' }}
                          readOnly={readOnly}
                        >
                          {Object.entries(xmlMapItemToOptions).map((entry, j) => (
                            <Option key={entry[0]} value={j}>
                              {t(entry[1])}
                            </Option>
                          ))}
                        </Select>
                      </div>
                    </Col>
                  </Row>
                ))}
              </Form.Item>
            </Tabs.TabPane>
          </Tabs>
        </div>
        <div className="form-buttons-container">
          <FormButtons
            readOnly={readOnly}
            onCancel={this.props.onCancel}
            onSubmit={this.handleSubmit}
            saveButtonLoading={this.state.saveButtonLoading}
            saveAndCloseButtonLoading={this.state.saveAndCloseButtonLoading}
          />
        </div>
      </Form>
    )
  }
}

export default Form.create()(FormView)
